import { Inject, Injectable, OnDestroy } from '@angular/core';

import { debounceTime, map, takeUntil } from 'rxjs/operators';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, Subject } from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import {
  WS_STATUS,
  IChatDraft,
  IChatMessage,
  IChatMessageError,
  CHAT_MESSAGE_TYPE,
  IChatOutgoingMessage,
  chatErrorDetailToText,
  NetworkStatusService,
} from '@cakeiteasy/web-utils';

import { ChatSocketService } from './chat-socket.service';
import { mergeChatMessages } from '../helpers/index';
import { CHAT_CONVERSATION_GATEWAY, IChatConversationGateway } from './chat-conversation-gateway';

const ECHO_TIMEOUT_MS = 10000;
const MARK_READ_DEBOUNCE_MS = 1000;
const RESUME_DEBOUNCE_MS = 500;
/** Must exist in every app that provides this manager. */
const SEND_FAILED_TRANSLATION_KEY = 'common.chat.sendFailed';

/**
 * Owns one conversation's state: REST history, live socket messages, connection status
 * and send errors.
 *
 * Holds per-conversation state, so it must be provided at component level, never in root.
 */
@Injectable()
export class OrderChatManagerService implements OnDestroy {
  // Read-only to everyone outside: this service is the only thing allowed to say what the
  // conversation's state is.
  public readonly messages$: Observable<IChatMessage[]>;
  public readonly connectionLost$: Observable<boolean>;
  public readonly reconnecting$: Observable<boolean>;
  public readonly sendError$: Observable<string>;
  public readonly loadError$: Observable<boolean>;
  /** True from the moment a message is handed over until the server confirms or refuses it. */
  public readonly sending$: Observable<boolean>;
  /**
   * Bumped once per confirmed send. The composer clears on this rather than inferring success
   * from the message list, which cannot tell our own echo apart from a message this customer
   * sent in another tab.
   */
  public readonly draftResetKey$: Observable<number>;

  private readonly _history$ = new BehaviorSubject<IChatMessage[]>([]);
  private readonly _live$ = new BehaviorSubject<IChatMessage[]>([]);
  private readonly _connectionLost$ = new BehaviorSubject<boolean>(false);
  private readonly _reconnecting$ = new BehaviorSubject<boolean>(false);
  private readonly _sendError$ = new BehaviorSubject<string>(null);
  private readonly _loadError$ = new BehaviorSubject<boolean>(false);
  private readonly _sending$ = new BehaviorSubject<boolean>(false);
  private readonly _draftResetKey$ = new BehaviorSubject<number>(0);
  private readonly _markRead$ = new Subject<void>();
  private readonly _destroy$ = new Subject<void>();

  private _messageType = CHAT_MESSAGE_TYPE.CUSTOMER;
  private _hasConnected = false;
  private _loadVersion = 0;
  private _echoTimer: ReturnType<typeof setTimeout> = null;
  private _parentOrderId: number = null;
  private _customerKey: string | number = null;

  constructor(
    private _chatSocket: ChatSocketService,
    @Inject(CHAT_CONVERSATION_GATEWAY) private _gateway: IChatConversationGateway,
    private _translate: TranslateService,
    private _networkStatus: NetworkStatusService,
  ) {
    this.messages$ = combineLatest([this._history$, this._live$]).pipe(
      map(([history, live]) => mergeChatMessages(history, live)),
    );

    this.connectionLost$ = this._connectionLost$.asObservable();
    this.reconnecting$ = this._reconnecting$.asObservable();
    this.sendError$ = this._sendError$.asObservable();
    this.loadError$ = this._loadError$.asObservable();
    this.sending$ = this._sending$.asObservable();
    this.draftResetKey$ = this._draftResetKey$.asObservable();

    this._chatSocket.messages$.pipe(takeUntil(this._destroy$)).subscribe((frame) => this._onMessageFrame(frame));

    this._chatSocket.errors$.pipe(takeUntil(this._destroy$)).subscribe((frame) => this._onErrorFrame(frame));

    this._chatSocket.status$.pipe(takeUntil(this._destroy$)).subscribe((status) => this._onStatus(status));

    this._markRead$
      .pipe(debounceTime(MARK_READ_DEBOUNCE_MS), takeUntil(this._destroy$))
      .subscribe(() => this._markRead());

    // Debounced because unlocking a phone can fire both signals at once, and each reopen
    // throws away the channel the previous one just created.
    this._networkStatus.resumed$
      .pipe(debounceTime(RESUME_DEBOUNCE_MS), takeUntil(this._destroy$))
      .subscribe(() => this._reopenSocket());
  }

  ngOnDestroy() {
    this._clearEchoTimeout();
    this._destroy$.next();
    this._destroy$.complete();
  }

  /**
   * Loads history, then opens the socket. Realtime needs both halves of the socket's address, so
   * a conversation missing either one stays on REST — which still loads and sends.
   */
  public async open(parentOrderId: number, customerKey?: string | number): Promise<void> {
    this._parentOrderId = parentOrderId;
    this._customerKey = customerKey;

    if (this._canUseSocket()) {
      this._chatSocket.connect(parentOrderId, customerKey);
    }

    await this._load(++this._loadVersion);
  }

  public async sendMessage(draft: IChatDraft): Promise<void> {
    const message: IChatOutgoingMessage = {
      type: this._messageType,
      text: draft.text,
      attachments: draft.attachments || [],
    };

    this._sendError$.next(null);
    this._sending$.next(true);

    if (this._chatSocket.send(message)) {
      // The socket took it; the message renders when its echo arrives.
      this._startEchoTimeout();
      return;
    }

    try {
      const messages = await firstValueFrom(this._gateway.sendMessage(message));
      this._history$.next(messages || []);
      this._live$.next([]);
      this._onSendConfirmed();
    } catch (error) {
      this._onSendFailed(this._translate.instant(SEND_FAILED_TRANSLATION_KEY));
    }
  }

  private _onMessageFrame(frame: IChatMessage) {
    this._live$.next([...this._live$.value, frame]);

    if (frame.type !== this._messageType) {
      this._markRead$.next();
      return;
    }

    // Our own type is not proof that this frame answers our send: the same customer sending from
    // another tab produces an identical frame. Only a pending send can be confirmed by one.
    if (this._sending$.value) {
      this._onSendConfirmed();
    }
  }

  private _onErrorFrame(frame: IChatMessageError) {
    this._onSendFailed(chatErrorDetailToText(frame.detail) || this._translate.instant(SEND_FAILED_TRANSLATION_KEY));
  }

  /** The one place a send stops being pending successfully, so the composer clears exactly here. */
  private _onSendConfirmed() {
    this._clearEchoTimeout();
    this._sending$.next(false);
    this._sendError$.next(null);
    this._draftResetKey$.next(this._draftResetKey$.value + 1);
  }

  /** The draft is deliberately kept: a failed send must not cost the customer their text. */
  private _onSendFailed(error: string) {
    this._clearEchoTimeout();
    this._sending$.next(false);
    this._sendError$.next(error);
  }

  /**
   * Reopens a socket that is not carrying messages any more.
   *
   * A channel that has given up never comes back on its own, and one still counting down its
   * backoff has nothing to lose by starting over: the network just changed under it. Anything
   * currently OPEN is left alone — a reopen there would drop frames for nothing.
   */
  private _reopenSocket() {
    if (!this._canUseSocket() || this._chatSocket.status$.value === WS_STATUS.OPEN || !this._networkStatus.isOnline) {
      return;
    }

    this._chatSocket.connect(this._parentOrderId, this._customerKey);
  }

  /** Realtime needs both halves of the socket's address; without them the conversation is REST-only. */
  private _canUseSocket(): boolean {
    return Boolean(this._parentOrderId && this._customerKey);
  }

  private _onStatus(status: WS_STATUS) {
    this._connectionLost$.next(status === WS_STATUS.FAILED);
    this._reconnecting$.next(status === WS_STATUS.RECONNECTING);

    if (status !== WS_STATUS.OPEN) {
      return;
    }

    // A reconnect replays nothing, so anything sent while the socket was down would stay
    // invisible until reload. The first open is already covered by the initial load.
    if (this._hasConnected) {
      this._load(++this._loadVersion);
    }

    this._hasConnected = true;
  }

  private async _load(loadVersion: number): Promise<void> {
    try {
      await firstValueFrom(this._gateway.markRead());
      const messages = await firstValueFrom(this._gateway.getMessages());

      // A slower earlier load must not overwrite a newer one.
      if (loadVersion !== this._loadVersion) {
        return;
      }

      // `_live$` is deliberately not cleared: a frame arriving between the fetch and this
      // assignment would be lost. Dedupe by id absorbs whatever overlaps.
      this._history$.next(messages || []);
      this._loadError$.next(false);
    } catch (error) {
      if (loadVersion !== this._loadVersion) {
        return;
      }

      this._loadError$.next(true);
    }
  }

  private _markRead() {
    firstValueFrom(this._gateway.markRead()).catch(() => {});
  }

  private _startEchoTimeout() {
    this._clearEchoTimeout();
    this._echoTimer = setTimeout(() => {
      this._onSendFailed(this._translate.instant(SEND_FAILED_TRANSLATION_KEY));
    }, ECHO_TIMEOUT_MS);
  }

  private _clearEchoTimeout() {
    if (this._echoTimer) {
      clearTimeout(this._echoTimer);
      this._echoTimer = null;
    }
  }
}
